import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useCompletePartnerTaskMutation,
  useGetPartnerTaskRewardMutation,
} from '@/api/queries/user';
import { defaultToastConfig } from '@/common/config/toast';
import { type PartnerTaskDto } from '@/common/types/query/tasks';
import { type CurrencyType } from '@/common/types/user';
import { H2 } from '@/components/Typography/Typography';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';
import { ValueTag } from '@/components/ui/ValueTag/ValueTag';
import { addToBalance } from '@/redux/reducers/userReducer';
import { useAppDispatch } from '@/redux/store';

import TaskButton from '../TaskButton/TaskButton';

import css from './style.module.scss';

const PartnersTaskItem: React.FC<PartnerTaskDto> = ({
  id,
  title,
  link,
  reward,
  icon,
  status,
  delayTimer,
}) => {
  const dispatch = useAppDispatch();
  const [completeTask] = useCompletePartnerTaskMutation();
  const [claimTaskReward] = useGetPartnerTaskRewardMutation();
  const [isMessageView, setIsMessageView] = useState(false);
  const [waitResponse, setWaitResponse] = useState(false);
  const { t } = useTranslation();
  useEffect(() => {
    if (delayTimer > 0) setIsMessageView(true);

    if (status === 'complete' && delayTimer === 0 && isMessageView) {
      showToast(
        t('You successfully completed the task!'),
        { img: icon ? icon : '/img/navigation/tasks.png' },
        {
          ...defaultToastConfig,
          className: 'justify-center',
        },
      );
      setIsMessageView(false);
    } else if (status === 'pending' && delayTimer === 0 && isMessageView) {
      showToast(
        `${t('Task requirements not met.')} ${title}`,
        { img: icon ? icon : '/img/navigation/tasks.png' },
        {
          ...defaultToastConfig,
          className: 'justify-center',
        },
      );
      setIsMessageView(false);
    }
  }, [delayTimer]);

  const handleClick = async (): Promise<void> => {
    if (status === 'complete' && delayTimer === 0) {
      await onClaimReward();

      return;
    }

    if (status !== 'paid' && delayTimer === 0) {
      await completeTask({ taskId: id });
    }

    if (link.includes('t.me')) {
      Telegram.WebApp.openTelegramLink(link);
    } else {
      Telegram.WebApp.openLink(link);
    }
  };

  const onClaimReward = async (): Promise<void> => {
    try {
      setWaitResponse(true);
      await claimTaskReward({ taskId: id })
        .unwrap()
        .then((data) => {
          if (data.status === 'paid') {
            dispatch(addToBalance(reward));
            showToast(
              t('The reward has been successfully received!'),
              { img: icon ? icon : '/img/navigation/tasks.png' },
              {
                ...defaultToastConfig,
                className: 'justify-center',
              },
            );
          } else {
            showToast(
              `${t('Task requirements not met.')} ${title}`,
              { img: icon ? icon : '/img/navigation/tasks.png' },
              {
                ...defaultToastConfig,
                className: 'justify-center',
              },
            );
          }
        });
    } catch (error: unknown) {
      console.error(error);
      showToast(t('The task is not yet completed!'), undefined, {
        ...defaultToastConfig,
        className: 'justify-center',
      });
    } finally {
      setWaitResponse(false);
    }
  };

  const createIcon = icon ? icon : '/img/navigation/tasks.png';

  return (
    <div key={id} className={css.partnerBlockTasks} onClick={handleClick}>
      <img src={createIcon} alt="icon" width={40} />
      <div className={css.partnerBlockTasksTitle}>
        <H2>{title}</H2>
        <div>
          {Object.entries(reward).map(([key, value]) => (
            <ValueTag
              variant="reward"
              value={value}
              kind={key as CurrencyType}
            />
          ))}
        </div>
      </div>
      <div className="w-[5.25rem] h-[2rem] absolute top-1/2 right-0 transform -translate-y-1/2 flex items-center justify-end">
        {status === 'paid' ? (
          <svg id="icon-checkmark" viewBox="0 0 32 32" width={20}>
            <path
              style={{ fill: '#82e024' }}
              d="M27 4l-15 15-7-7-5 5 12 12 20-20z"
            ></path>
          </svg>
        ) : (
          <TaskButton
            isWaitForClaim={delayTimer > 0}
            waitResponse={waitResponse}
            timer={delayTimer}
            isClaimButtonActive={status === 'complete' && delayTimer === 0}
          />
        )}
      </div>
    </div>
  );
};

export default PartnersTaskItem;
